import { useState } from "react";
import { Settings as SettingsIcon, Shield, User, LogOut, KeyRound, Database, Wrench, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LogoWordmark } from "@/components/Logo";
import { ChangePasswordForm } from "@/components/ChangePasswordForm";
import MaintenanceSettings from "@/components/MaintenanceSettings";
import MasterDataSettings from "@/components/MasterDataSettings";
import { initials } from "@/lib/format";

const PERMS = {
  Admin: ["Import leads", "Manage users & rules", "Assign / reassign leads", "Delete leads", "View all leads", "Re-run assignment"],
  "Sales Manager": ["View all leads", "Assign / reassign leads", "Edit leads", "Update follow-ups", "View dashboard & rules"],
  "Sales Executive": ["View assigned leads only", "Update stage, quality, notes & follow-ups", "Cannot import or delete"],
};

function AccountTab({ user, logout }) {
  const [showPw, setShowPw] = useState(false);
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-sky/70 bg-white p-6">
        <div className="flex items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-navy text-xl font-bold text-white">{initials(user?.name)}</span>
          <div>
            <h2 className="font-sora text-lg font-semibold text-navy">{user?.name}</h2>
            <p className="text-sm text-ink/60">{user?.email}</p>
            <span className="mt-1 inline-block rounded-md bg-sky/60 px-2 py-0.5 text-xs font-medium text-navy">{user?.role}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-sky/70 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 font-sora font-semibold text-navy"><Shield className="h-5 w-5 text-loop" />Your Permissions</h2>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(PERMS[user?.role] || []).map((p) => (
            <li key={p} className="flex items-center gap-2 rounded-lg bg-mist px-3 py-2 text-sm text-ink/80">
              <span className="h-1.5 w-1.5 rounded-full bg-loop" />{p}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border border-sky/70 bg-white p-6" data-testid="security-section">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-sora font-semibold text-navy"><KeyRound className="h-5 w-5 text-loop" />Password</h2>
          {!showPw && (
            <button onClick={() => setShowPw(true)} data-testid="open-change-password" className="rounded-xl border border-sky px-3.5 py-2 text-sm font-medium text-loop hover:border-loop">Change password</button>
          )}
        </div>
        {showPw && (
          <div className="mt-5 max-w-md">
            <ChangePasswordForm onSuccess={() => setShowPw(false)} />
            <button onClick={() => setShowPw(false)} className="mt-3 text-xs font-medium text-ink/50 hover:text-ink/80">Cancel</button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-sky/70 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 font-sora font-semibold text-navy"><User className="h-5 w-5 text-loop" />About</h2>
        <LogoWordmark className="h-8" />
        <p className="mt-3 text-sm text-ink/60">AcquireLoop is a premium lead routing, qualification and follow-up management system built for the Proffus growth team.</p>
        <p className="mt-2 text-xs text-ink/40">Version 1.0 · AcquireLoop by Proffus</p>
      </div>

      <button onClick={logout} data-testid="settings-logout" className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-100">
        <LogOut className="h-4 w-4" />Log out
      </button>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [tab, setTab] = useState("account");

  const tabs = [
    { key: "account", label: "My Account", icon: UserCircle },
    ...(isAdmin ? [
      { key: "master", label: "Master Data", icon: Database },
      { key: "maintenance", label: "Maintenance", icon: Wrench },
    ] : []),
  ];

  return (
    <div className={`mx-auto space-y-6 ${tab === "master" ? "max-w-6xl" : "max-w-3xl"}`} data-testid="settings-page">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><SettingsIcon className="h-7 w-7 text-loop" />Settings</h1>
        <p className="text-sm text-ink/60">Your account and workspace configuration.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1.5 rounded-xl border border-sky/70 bg-white p-1.5" data-testid="settings-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`settings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-loop text-white" : "text-ink/60 hover:bg-mist"}`}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      {tab === "account" && <AccountTab user={user} logout={logout} />}
      {tab === "master" && isAdmin && <MasterDataSettings />}
      {tab === "maintenance" && isAdmin && <MaintenanceSettings />}
    </div>
  );
}
